import React, { useEffect, useRef } from 'react';

const RUNES = 'ᚠᚢᚦᚨᚱᚲᚷᚹᚺᚾᛁᛃᛇᛈᛉᛊᛏᛒᛖᛗᛚᛜᛞᛟ'.split('');
const N_OUTER = 48;
const N_INNER = 30;

export default function BtcRingLogo({ size = 54 }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    const cv = canvasRef.current;
    if (!cv) return;
    const dpr = window.devicePixelRatio || 1;
    const renderSize = Math.round(size * 1.28); // extra room for glow
    cv.width = renderSize * dpr;
    cv.height = renderSize * dpr;
    const ctx = cv.getContext('2d');
    ctx.scale(dpr, dpr);

    const W = renderSize, H = renderSize;
    const cx = W / 2, cy = H / 2;
    const R = Math.min(W, H) * 0.30;
    const N_SEGS = 9;
    const segP = new Array(N_SEGS).fill(0);
    const outerRunes = Array.from({ length: N_OUTER }, (_, i) => RUNES[i % RUNES.length]);
    const innerRunes = Array.from({ length: N_INNER }, (_, i) => RUNES[(i * 2) % RUNES.length]);

    let frame = 0;
    let runeAngle = 0;
    let phase = 0;
    let phaseT = 0;
    const DUR = [30, 90, 9999];

    // Pre-render ₿ glyph onto offscreen canvas
    const bCv = document.createElement('canvas');
    bCv.width = bCv.height = 128;
    const bc = bCv.getContext('2d');
    bc.fillStyle = '#ff8c00';
    bc.shadowColor = '#ff6600';
    bc.shadowBlur = 12;
    bc.font = 'bold 96px Arial';
    bc.textAlign = 'center';
    bc.textBaseline = 'middle';
    bc.fillText('₿', 64, 68);

    function loop() {
      frame++; phaseT++;
      if (phase < 2 && phaseT > DUR[phase]) { phase++; phaseT = 0; }
      const pg = Math.min(phaseT / DUR[1], 1);

      ctx.clearRect(0, 0, W, H);

      // Ring (phase 1+)
      if (phase >= 1) {
        const si = Math.floor(pg * N_SEGS);
        for (let i = 0; i < N_SEGS; i++) {
          if (i < si) segP[i] = Math.min(segP[i] + 0.07, 1);
          else if (i === si) segP[i] = Math.min(segP[i] + 0.035, 1);
        }

        ctx.save();
        ctx.shadowColor = '#ffaa00';
        ctx.shadowBlur = 6;
        [
          [R * 1.10, 0.5, 0.45], [R * 1.04, 0.5, 0.35],
          [R,        2,   0.95], [R * 0.96, 0.7, 0.75],
          [R * 0.89, 0.5, 0.45], [R * 0.83, 1.5, 0.80],
          [R * 0.77, 0.5, 0.40],
        ].forEach(([r, lw, a]) => {
          ctx.strokeStyle = `rgba(255,150,0,${a})`;
          ctx.lineWidth = lw;
          ctx.beginPath();
          ctx.arc(cx, cy, r, 0, Math.PI * 2);
          ctx.stroke();
        });
        ctx.restore();

        // Radial glow
        const og = ctx.createRadialGradient(cx, cy, R * 0.88, cx, cy, R * 1.15);
        og.addColorStop(0, 'rgba(255,120,0,0)');
        og.addColorStop(0.5, 'rgba(255,140,0,0.10)');
        og.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.fillStyle = og;
        ctx.beginPath();
        ctx.arc(cx, cy, R * 1.2, 0, Math.PI * 2);
        ctx.fill();

        // Lightning bolt arrows
        for (let i = 0; i < N_SEGS; i++) {
          const sp = segP[i]; if (sp <= 0) continue;
          const a = (i / N_SEGS) * Math.PI * 2 - Math.PI / 2;
          const nx = cx + Math.cos(a) * R;
          const ny = cy + Math.sin(a) * R;
          ctx.save();
          ctx.translate(nx, ny);
          ctx.rotate(a + Math.PI / 2);
          ctx.shadowColor = '#ffee00';
          ctx.shadowBlur = sp * 8;
          const s = R * 0.09 * sp;
          ctx.fillStyle = `rgba(255,${180 + sp * 75},0,${sp})`;
          ctx.beginPath();
          ctx.moveTo(0, -s * 1.3);
          ctx.lineTo(-s * 0.6, s * 0.4);
          ctx.lineTo(-s * 0.25, s * 0.2);
          ctx.lineTo(0, s * 0.65);
          ctx.lineTo(s * 0.25, s * 0.2);
          ctx.lineTo(s * 0.6, s * 0.4);
          ctx.closePath();
          ctx.fill();
          ctx.restore();
        }
      }

      // Runes (phase 2)
      if (phase >= 2) {
        runeAngle += 0.011;
        const ra = Math.min(phaseT / 40, 1);
        const rFS = Math.max(3, Math.round(R * 0.088));
        ctx.font = `${rFS}px serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        for (let i = 0; i < N_OUTER; i++) {
          const a = runeAngle + (i / N_OUTER) * Math.PI * 2;
          const rx = cx + Math.cos(a) * R * 0.92;
          const ry = cy + Math.sin(a) * R * 0.92;
          const flk = 0.55 + Math.sin(frame * 0.25 + i * 1.9) * 0.45;
          ctx.save();
          ctx.translate(rx, ry);
          ctx.rotate(a + Math.PI / 2);
          ctx.fillStyle = `rgba(255,${130 + i % 60},0,${ra * flk})`;
          ctx.shadowColor = '#ff8800';
          ctx.shadowBlur = 3;
          ctx.fillText(outerRunes[i], 0, 0);
          ctx.restore();
        }

        // ₿ coin (pulsing opacity, simple 2D)
        const coinOpacity = Math.min(phaseT / 40, 1);
        const coinAngle = frame * 0.018;
        // Simulate depth by scaling X with cos
        const scaleX = Math.abs(Math.cos(coinAngle)) * 0.6 + 0.4;
        const sz = R * 1.35;
        ctx.save();
        ctx.globalAlpha = coinOpacity;
        ctx.shadowColor = '#ff8800';
        ctx.shadowBlur = 8;
        ctx.translate(cx, cy);
        ctx.scale(scaleX, 1);
        ctx.drawImage(bCv, -sz / 2, -sz / 2, sz, sz);
        ctx.restore();
      }

      rafRef.current = requestAnimationFrame(loop);
    }

    loop();
    return () => cancelAnimationFrame(rafRef.current);
  }, [size]);

  return (
    <canvas
      ref={canvasRef}
      style={{ width: size + 'px', height: size + 'px', display: 'block', flexShrink: 0 }}
    />
  );
}
